import { useState, useRef, useEffect } from 'react'
import { Bot, Send, User, Loader2, Check, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

// Admin customer-success chat assistant — a thin chat UI over
// POST /admin/assistant/chat (Grok tool-calling loop, read-only + propose
// only) and POST /admin/assistant/execute (the actual bulk_approve/
// bulk_reject, run only after an admin explicitly confirms a proposed
// action here). Deliberately simple/self-contained: this is an internal
// tool, not a customer-facing surface, and doesn't share state with
// UsersSection — a toast confirmation on execute is enough.

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ProposedAction {
  action: 'bulk_approve' | 'bulk_reject'
  user_ids: string[]
  summary: string
}

function actionLabel(action: ProposedAction['action']) {
  return action === 'bulk_approve' ? 'Bulk Approve' : 'Bulk Reject'
}

export default function AssistantSection() {
  const { toast } = useToast()
  const [history, setHistory] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [pendingActions, setPendingActions] = useState<ProposedAction[]>([])
  const [confirmingIndex, setConfirmingIndex] = useState<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [history, loading])

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed || loading) return

    const newHistory: ChatMessage[] = [...history, { role: 'user', content: trimmed }]
    setHistory(newHistory)
    setInput('')
    setLoading(true)
    try {
      const res = await apiClient.adminAssistantChat(newHistory)
      setHistory([...newHistory, { role: 'assistant', content: res.reply }])
      if (res.proposed_actions?.length) {
        setPendingActions((prev) => [...prev, ...res.proposed_actions])
      }
    } catch (e: any) {
      toast({ title: 'Assistant request failed', description: e?.message, variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const handleConfirm = async (action: ProposedAction, index: number) => {
    setConfirmingIndex(index)
    try {
      const result = await apiClient.adminAssistantExecute({ action: action.action, user_ids: action.user_ids })
      const parts = [`${result.updated.length} succeeded`]
      if (result.failed.length) parts.push(`${result.failed.length} failed`)
      toast({
        title: parts.join(', '),
        description: result.failed.length ? result.failed.map((f) => `${f.user_id}: ${f.error}`).join('; ') : undefined,
        variant: result.failed.length && !result.updated.length ? 'destructive' : undefined,
      })
      setPendingActions((prev) => prev.filter((_, i) => i !== index))
    } catch (e: any) {
      toast({ title: 'Action failed', description: e?.message, variant: 'destructive' })
    } finally {
      setConfirmingIndex(null)
    }
  }

  const handleDismiss = (index: number) => {
    setPendingActions((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Customer-Success Assistant
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Ask about the pending-approval backlog, stale-approved users, or search for a user. Bulk approve/reject
            actions are only ever proposed here — nothing runs until you confirm it below.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div ref={scrollRef} className="h-[420px] overflow-y-auto rounded-md border p-4 space-y-3">
            {history.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-12">
                Try: "How many users are pending approval?" or "Show me the oldest pending signups."
              </p>
            )}
            {history.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`flex max-w-[80%] items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  <div className="mt-0.5 shrink-0 rounded-full bg-muted p-1.5">
                    {msg.role === 'user' ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                  </div>
                  <div
                    className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                      msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking...
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask the assistant..."
              disabled={loading}
            />
            <Button onClick={() => void handleSend()} disabled={loading || !input.trim()}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {pendingActions.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">Proposed actions awaiting confirmation</h3>
          {pendingActions.map((action, idx) => (
            <Card key={idx} className="border-amber-500/50">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant={action.action === 'bulk_reject' ? 'destructive' : 'default'}>
                      {actionLabel(action.action)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">{action.user_ids.length} user(s)</span>
                  </div>
                  <p className="text-sm">{action.summary}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={action.action === 'bulk_reject' ? 'destructive' : 'default'}
                    disabled={confirmingIndex === idx}
                    onClick={() => handleConfirm(action, idx)}
                  >
                    {confirmingIndex === idx ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="mr-1 h-4 w-4" />
                    )}
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={confirmingIndex === idx}
                    onClick={() => handleDismiss(idx)}
                  >
                    <X className="mr-1 h-4 w-4" />
                    Dismiss
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
